import React, {useContext, useEffect} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import './css/style.css';
import './mosaic/charts/ChartjsConfig';
import {GlobalContext} from "./utils/context/GlobalContextProvider.jsx";
import Routing from "./Routing.jsx";
import i18next from "i18next";
import {initReactI18next} from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import translations_cs_CZ from "../translations/translations.cs-CZ.json";
import {SystemApi, UserApi} from "./api/authority/generated/index.ts";
import BigLoader from "./components/loader/BigLoader.jsx";
import {getLocalStorageUpgatesPendingToken, setLocalStorageUpgatesPendingToken} from "./utils/localStorage.js";

function DatixoApplication() {
    const {
        context,
        currentProjectId,
        setLocalizationLoaded,
        setCurrentIdentity,
        addCurrentUserProject,
        setCurrentUserProjectsLoaded,
        setCurrentUserProjectsReload,
        getAuthorityConfiguration,
        setDefaultSubscriptionValues,
        setDefaultSubscriptionValuesLoaded,
        setSupportedCurrencies,
        setSupportedCurrenciesLoaded,
        setPermissions,
    } = useContext(GlobalContext);

    const authorityConfiguration = getAuthorityConfiguration();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (searchParams.get('reset_token') !== '1') {
            return;
        }

        setLocalStorageUpgatesPendingToken(null);

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('reset_token');
        setSearchParams(nextSearchParams, {replace: true});
        navigate('/', {replace: true});
    }, [navigate, searchParams, setSearchParams]);

    useEffect(() => {
        const upgatesToken = getUpgatesTokenFromSearch(searchParams, location.search);

        if (upgatesToken === null) {
            return;
        }

        setLocalStorageUpgatesPendingToken(upgatesToken);

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('upgates_token');
        nextSearchParams.delete('lang');
        for (const key of [...nextSearchParams.keys()]) {
            if (key.startsWith('upgates_token')) {
                nextSearchParams.delete(key);
            }
        }
        setSearchParams(nextSearchParams, {replace: true});
    }, [location.search, searchParams, setSearchParams]);

    useEffect(() => {
        const hasPendingUpgatesToken = getLocalStorageUpgatesPendingToken() !== null;

        if (context.currentIdentity || !hasPendingUpgatesToken) {
            return;
        }

        if (
            [
                '/sign/in/addon',
                '/reset-password',
                '/sign/up/continue-with-email',
                '/sign/up/email',
                '/invite',
            ].includes(location.pathname)
        ) {
            return;
        }

        navigate('/sign/in/addon', {replace: true});
    }, [context.currentIdentity, location.pathname, navigate]);

    // DataLayer - user and project information
    useEffect(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'pageView',
            project: context.currentProjectName,
            userId: context.currentIdentity?.user?.id,
            path: location.pathname,
        });
    }, [location.pathname, context.currentProjectName, context.currentIdentity]);

    // Facebook SDK
    useEffect(() => {
        window.fbAsyncInit = () => {
            window.FB.init({
                appId: import.meta.env.VITE_META_APP_ID,
                autoLogAppEvents: true,
                xfbml: true,
                version: 'v19.0'
            });
        };

        (function (d, s, id) {
            let js, fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) return;
            js = d.createElement(s);
            js.id = id;
            js.src = "https://connect.facebook.net/en_US/sdk.js";
            fjs.parentNode.insertBefore(js, fjs);
        }(document, 'script', 'facebook-jssdk'));
    }, []);

    // Load translations
    useEffect(() => {
        i18next
            .use(initReactI18next)
            .use(LanguageDetector)
            .use(resourcesToBackend(locateTranslation))
            .init({
                compatibilityJSON: 'v3',
                ns: ['translations'],
                lngs: ['cs-CZ'],
                defaultNs: 'translations',
                defaultLng: 'cs-CZ',
                fallbackLng: 'cs-CZ',
            })
            .then(() => setLocalizationLoaded(true));
    }, []);

    // Check user identity and log out on invalid token
    useEffect(() => {
        if (
            Object.keys(authorityConfiguration.headers).length === 0 ||
            !context.currentIdentity
        ) {
            return;
        }

        new UserApi(authorityConfiguration)
            .userDetail({userId: context.currentIdentity?.user.id})
            .catch(() => setCurrentIdentity(null));
    }, [context.currentIdentity]);

    // Load user projects only once if logged in
    useEffect(() => {
        const tokenForProject = context.currentIdentity?.token ?? null;

        if (!context.currentIdentity?.user.id) {
            setCurrentUserProjectsLoaded(true, tokenForProject); // No user, mark as loaded
            return;
        }

        let userId = context.currentIdentity.user.id;

        async function fetchProjects() {
            //let allProjects = [];

            const api = new UserApi(authorityConfiguration);

            let page = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const response = await api.userProjects({userId, page});
                response.data.forEach(project => addCurrentUserProject(project, tokenForProject));
                hasNextPage = response.pagination.hasNextPage;
                page += 1;
            }

            setCurrentUserProjectsLoaded(true, tokenForProject);


        }

        // noinspection JSIgnoredPromiseFromCall
        fetchProjects();

        /* */

        //  return allProjects;
    }, [context.currentIdentity, context.currentUserProjectsReload]);

    // Handle project reload
    useEffect(() => {
        if (!context.currentUserProjectsReload) return;

        const tokenForProject = context.currentIdentity?.token ?? null;
        setCurrentUserProjectsLoaded(false, tokenForProject);
        setCurrentUserProjectsReload(false);
    }, [context.currentUserProjectsReload]);

    // Scroll to top on route change
    useEffect(() => {
        document.querySelector('html').style.scrollBehavior = 'auto';
        window.scroll({top: 0});
        document.querySelector('html').style.scrollBehavior = '';
    }, [location.pathname]);

    // Set permissions
    useEffect(() => {
        new SystemApi(getAuthorityConfiguration())
            .projectPermissions()
            .then(r => setPermissions(r.permissions))
            .catch(e => console.error(e));
    }, []);

    // Default subscription values
    useEffect(() => {
        new SystemApi(getAuthorityConfiguration())
            .defaultSubscriptionValues()
            .then(r => {
                setDefaultSubscriptionValues(r);
                setDefaultSubscriptionValuesLoaded();
            })
            .catch(e => console.error(e));
    }, []);

    // Supported currencies
    useEffect(() => {
        new SystemApi(getAuthorityConfiguration())
            .supportedCurrencies()
            .then(r => {
                setSupportedCurrencies(r);
                setSupportedCurrenciesLoaded();
            })
            .catch(e => console.error(e));
    }, []);

    // Redirect logic for onboarding if user is logged in
    useEffect(() => {
        if (!context.currentIdentity) return;

        const isOnboardingRoute = location.pathname.startsWith('/onboarding');
        const hasPendingUpgatesToken = getLocalStorageUpgatesPendingToken() !== null;

        if (
            location.pathname === '/onboarding/addon-project' &&
            !hasPendingUpgatesToken &&
            context.currentUserProjectsLoaded &&
            context.currentUserProjectsReload === false
        ) {
            navigate(context.currentUserProjects.length === 0 ? '/onboarding/project' : '/', {replace: true});
            return;
        }

        if (
            hasPendingUpgatesToken &&
            context.currentUserProjectsLoaded &&
            context.currentUserProjects.length === 0 &&
            context.currentUserProjectsReload === false &&
            location.pathname === '/onboarding/project'
        ) {
            navigate('/onboarding/addon-project');
            return;
        }

        if (isOnboardingRoute) return;

        if (context.currentIdentity.user.newPasswordRequired) {
            navigate('/onboarding/password');
        } else if (
            context.currentIdentity.user.firstName === '' ||
            context.currentIdentity.user.lastName === '' ||
            context.currentIdentity.user.phone === ''
        ) {
            navigate('/onboarding/info');
        } else if (
            context.currentUserProjectsLoaded &&
            context.currentUserProjects.length === 0 &&
            context.currentUserProjectsReload === false
        ) {
            navigate(hasPendingUpgatesToken ? '/onboarding/addon-project' : '/onboarding/project');
        } else if (
            context.currentUserProjectsLoaded &&
            context.currentUserProjects.length > 0 &&
            hasPendingUpgatesToken
        ) {
            navigate('/onboarding/addon-project');
        }
    }, [
        context.currentIdentity,
        context.currentUserProjects.length,
        context.currentUserProjectsLoaded,
        context.currentUserProjectsReload,
        location.pathname,
        navigate,
    ]);

    // Rendering logic
    if (!context.localizationLoaded) {
        return (
            <div className={'h-lvh flex justify-center'}>
                <BigLoader/>
            </div>
        );
    }

    if (!context.currentIdentity) {
        // If user is not logged in, directly show the login page
        return <Routing/>; // Assumes Routing handles /sign/in
    }

    if (
        context.currentUserProjectsLoaded &&
        context.permissionsLoaded &&
        context.defaultSubscriptionValuesLoaded &&
        context.supportedCurrenciesLoaded
    ) {
        return <Routing key={currentProjectId}/>;
    }

    return (
        <div className={'h-lvh flex justify-center'}>
            <BigLoader/>
        </div>
    );
}

function locateTranslation(language, namespace) {
    const translations = {
        'cs': {'translations': translations_cs_CZ},
        'cs-CZ': {'translations': translations_cs_CZ},
    };
    return translations[language]?.[namespace];
}

function getUpgatesTokenFromSearch(searchParams, rawSearch) {
    const queryToken = searchParams.get('upgates_token');

    if (queryToken !== null) {
        return queryToken;
    }

    const malformedTokenMatch = rawSearch.match(/[?&]upgates_token([^=&]+)/);

    if (!malformedTokenMatch) {
        return null;
    }

    return decodeURIComponent(malformedTokenMatch[1]);
}

export default DatixoApplication;
